import { ApiError } from '@/lib/projectsApi'
import type { DraftClaim, DraftPayload } from '@/lib/raDraftsApi'

interface DraftErrorDetail {
  estimate?: { cost_usd?: number; prompt_tokens?: number; completion_tokens?: number }
  max_cost_usd?: number
  gate?: { routing?: string; reasons?: string[]; warnings?: string[] }
}

interface DraftErrorBody {
  error?: string
  detail?: DraftErrorDetail
}

export function verifiedVesselCitationIds(claim: Pick<DraftClaim, 'citations'>): string[] {
  return [...new Set(
    claim.citations
      .filter(citation => citation.verified && citation.scope === 'vessel')
      .map(citation => citation.evidence_id),
  )]
}

export function draftFailedReason(payload: Pick<DraftPayload, 'draft'>): string | null {
  if (payload.draft.failed_reason?.trim()) return payload.draft.failed_reason
  const history = payload.draft.generation_history ?? []
  for (let i = history.length - 1; i >= 0; i--) {
    const event = history[i]
    if (event.action === 'failed' && typeof event.error === 'string' && event.error.trim()) {
      return event.error
    }
  }
  return null
}

export function draftDroppedFields(payload: Pick<DraftPayload, 'draft' | 'dropped_fields'>): string[] {
  if (payload.dropped_fields?.length) return payload.dropped_fields
  const history = payload.draft.generation_history ?? []
  for (let i = history.length - 1; i >= 0; i--) {
    const fields = history[i].dropped_fields
    if (Array.isArray(fields)) return fields.filter((field): field is string => typeof field === 'string')
  }
  return []
}

export function draftErrorMessage(error: unknown): string {
  const fallback = error instanceof Error ? error.message : String(error)
  if (!(error instanceof ApiError)) return fallback

  const body = error.body as DraftErrorBody | undefined
  const detail = body?.detail
  const lines = [body?.error ?? fallback]
  if (detail?.gate) {
    if (detail.gate.routing) lines.push(`Routing: ${detail.gate.routing}`)
    lines.push(...(detail.gate.reasons ?? []).map(reason => `• ${reason}`))
    lines.push(...(detail.gate.warnings ?? []).map(warning => `⚠ ${warning}`))
  }
  if (detail?.estimate?.cost_usd != null) {
    const cap = detail.max_cost_usd != null ? `; confirmed cap US$${Number(detail.max_cost_usd).toFixed(4)}` : ''
    lines.push(`Pre-flight estimate: US$${Number(detail.estimate.cost_usd).toFixed(4)}${cap}`)
  }
  return [...new Set(lines.filter(Boolean))].join('\n')
}
